import type { FC, FormEvent, ReactNode } from 'react'
import type { TreatmentOption } from '@/lib/clinic/types'

import { useState } from 'react'
import { toast } from 'sonner'
import { Check, Pencil, Plus, RotateCcw, Trash2, X } from 'lucide-react'
import { useClinic } from '@/hooks/use-clinic'
import { useAppActions } from '@/components/app/app-actions'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { SectionCard } from '@/components/app/common'

interface TreatmentRowProps {
    item: TreatmentOption
}

const TreatmentRow: FC<TreatmentRowProps> = ({ item }): ReactNode => {
    const { saveTreatment, deleteTreatment } = useClinic()
    const { confirm } = useAppActions()
    const [editing, setEditing] = useState(false)
    const [name, setName] = useState(item.name)
    const [error, setError] = useState<string | null>(null)

    const save = (): void => {
        const result = saveTreatment(name, item.id)

        if (!result.ok) {
            setError(result.error)

            return
        }

        setEditing(false)
        setError(null)
        toast.success('Treatment updated')
    }

    const remove = (): void => {
        confirm({
            title: `Remove "${item.name}"?`,
            description: 'It disappears from the quick choices. Past visits keep their text.',
            confirmLabel: 'Remove',
            destructive: true,
            onConfirm: () => {
                deleteTreatment(item.id)
                toast.success(`"${item.name}" removed`)
            }
        })
    }

    return (
        <li className='flex flex-wrap items-center gap-2 py-2.5'>
            {editing ? (
                <>
                    <Input
                        value={name}
                        onChange={event => {
                            setName(event.target.value)
                            setError(null)
                        }}
                        onKeyDown={event => {
                            if (event.key === 'Enter') {
                                event.preventDefault()
                                save()
                            }

                            if (event.key === 'Escape') {
                                setEditing(false)
                                setName(item.name)
                            }
                        }}
                        aria-label='Treatment name'
                        aria-invalid={Boolean(error)}
                        autoFocus
                        className='min-w-0 flex-1'
                    />

                    <Button type='button' size='icon-sm' onClick={save} aria-label='Save'>
                        <Check />
                    </Button>

                    <Button
                        type='button'
                        variant='ghost'
                        size='icon-sm'
                        onClick={() => {
                            setEditing(false)
                            setName(item.name)
                            setError(null)
                        }}
                        aria-label='Cancel editing'
                    >
                        <X />
                    </Button>

                    {error && (
                        <p role='alert' className='w-full text-xs font-medium text-destructive'>
                            {error}
                        </p>
                    )}
                </>
            ) : (
                <>
                    <span className='min-w-0 flex-1 truncate text-sm font-medium text-foreground'>
                        {item.name}
                    </span>

                    <Button type='button' variant='ghost' size='icon-sm' onClick={() => setEditing(true)} aria-label={`Edit ${item.name}`}>
                        <Pencil />
                    </Button>

                    <Button type='button' variant='ghost' size='icon-sm' onClick={remove} aria-label={`Remove ${item.name}`} className='text-muted-foreground hover:text-destructive'>
                        <Trash2 />
                    </Button>
                </>
            )}
        </li>
    )
}

export const TreatmentSettingsPanel: FC = (): ReactNode => {
    const { data, saveTreatment, resetTreatments } = useClinic()
    const { confirm } = useAppActions()
    const [name, setName] = useState('')
    const [error, setError] = useState<string | null>(null)

    const add = (event: FormEvent<HTMLFormElement>): void => {
        event.preventDefault()

        const result = saveTreatment(name)

        if (!result.ok) {
            setError(result.error)

            return
        }

        toast.success(`"${name.trim()}" added`)
        setName('')
        setError(null)
    }

    const restore = (): void => {
        confirm({
            title: 'Restore the default list?',
            description: 'Your custom entries are replaced with the 12 standard dental problems and treatments.',
            confirmLabel: 'Restore defaults',
            onConfirm: () => {
                resetTreatments()
                toast.success('Default treatments restored')
            }
        })
    }

    return (
        <SectionCard
            title='Treatments and dental problems'
            description='Quick choices when recording a visit, a complaint or a follow-up reason.'
            actions={(
                <Button type='button' variant='ghost' size='sm' onClick={restore}>
                    <RotateCcw />
                    Restore defaults
                </Button>
            )}
        >
            <form onSubmit={add} className='flex flex-wrap items-start gap-2'>
                <div className='grid min-w-0 flex-1 gap-1'>
                    <Input
                        value={name}
                        onChange={event => {
                            setName(event.target.value)
                            setError(null)
                        }}
                        placeholder='Add a treatment or problem, e.g. Dental Implant'
                        aria-label='New treatment name'
                        aria-invalid={Boolean(error)}
                    />

                    {error && (
                        <p role='alert' className='text-xs font-medium text-destructive'>
                            {error}
                        </p>
                    )}
                </div>

                <Button type='submit'>
                    <Plus />
                    Add
                </Button>
            </form>

            {data.treatments.length === 0 ? (
                <p className='mt-4 rounded-xl border border-dashed p-4 text-center text-sm text-muted-foreground'>
                    The list is empty. Add your own or restore the defaults.
                </p>
            ) : (
                <ul className='mt-3 divide-y'>
                    {data.treatments.map(item => (
                        <TreatmentRow key={`${item.id}-${item.name}`} item={item} />
                    ))}
                </ul>
            )}
        </SectionCard>
    )
}
